import { omit, uniqBy } from 'lodash'
import { Children, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import Loading from '@/components/Loading'
import { useAppSelector } from '@/store/hooks'
import cn from '@/utilities/cn'

import Header from './Header'

import './index.scss'

import ErrorComponent from '@/components/ErrorComponent'
import { getErrors } from '@/store/selectors'

import messages from './messages'

const matchesFunction = (sourceObj: any, otherObj: any) => {
  return Object.keys(sourceObj).reduce(
    (isMatch, prop) =>
      isMatch && ['array', 'object'].includes(typeof sourceObj[prop])
        ? sourceObj[prop].includes(otherObj[prop])
        : sourceObj[prop] === otherObj[prop],
    true
  )
}

const handleErrors = (errorsToHandle: HandledError[], errors: Error[]) => {
  const errorsWithIndex = errors.map((e, index) => omit({ ...e, errorMessage: e.message, index }, 'message'))
  const filteredErrors = uniqBy(
    errorsToHandle.flatMap(errorToHandle =>
      errorsWithIndex
        .filter(e => matchesFunction(omit(errorToHandle, 'data'), e))
        .map(e => ({
          ...e,
          ...errorToHandle.data
        }))
    ),
    'index'
  )

  return filteredErrors.length > 0 ? <ErrorComponent errors={filteredErrors} /> : null
}

type PageProps = {
  children?: ReactNode
  className?: string
  isLoading?: boolean
  loadingMessage?: ReactNode
  errorsToHandle?: HandledError[]
}

type HandledError = {
  type: string
  code?: string | number
  httpStatusCode?: number
  // TODO add the "error shape"
  data: object
}

function Page({
  children,
  className,
  isLoading = false,
  loadingMessage = <FormattedMessage {...messages.loading} />,
  errorsToHandle
}: PageProps) {
  const errors = useAppSelector(getErrors)
  const error = errors.length > 0 && errorsToHandle?.length && handleErrors(errorsToHandle, errors)

  // if there is a header we want to show it if there is an error
  const header = Children.toArray(children).find(c => c && typeof c === 'object' && 'type' in c && c.type === Header)

  return (
    <div className={cn('page-wrapper', className)}>
      <div className="flex-center">
        <div className={cn('content', 'fade-in', className)}>
          {error ? (
            <>
              {header}
              <div className="error">{error}</div>
            </>
          ) : isLoading ? (
            <Loading isLoading={isLoading} loadingMessage={loadingMessage} />
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  )
}

Page.Header = Header

export default Page

export { default as Header } from './Header'
